import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { HelpCircle, ChevronDown, Search, Sparkles, BookOpen, ShieldCheck, Clock, FileCode, CheckCircle2 } from 'lucide-react';

interface FaqItem {
  id: string;
  category: string;
  question: string;
  answer: string;
}

const FAQ_DATA: FaqItem[] = [
  {
    id: '1',
    category: 'Typesetting & Automation',
    question: 'How does ASAP LEARNING combine AI with traditional typesetting precision?',
    answer: 'ASAP LEARNING leverages AI-assisted automated workflow engines to ingest raw manuscripts, parse structural styles, and auto-populate templates in InDesign and LaTeX. Experienced human typographers then review typography, pagination, baseline grids, and line-breaks to guarantee 100% publisher-grade accuracy.'
  },
  {
    id: '2',
    category: 'Quality & Accuracy',
    question: 'What quality control measures ensure error-free XML and PDF deliverables?',
    answer: 'Our multi-tier QA protocol combines automated schema validators (BITS, JATS, DocBook, EPUB3) with rigorous manual proofing. Every equation, reference citation, and table is cross-checked against strict STM and ISO guidelines before final delivery.'
  },
  {
    id: '3',
    category: 'Turnaround & Capacity',
    question: 'What is the typical turnaround time for complex academic or technical books?',
    answer: 'Thanks to our automated prepress pipelines, standard turnaround times are reduced by up to 60%. A typical 300-page academic book or journal volume is completed within 3 to 7 business days, with express 24-48 hour turnaround available for urgent releases.'
  },
  {
    id: '4',
    category: 'File Formats & Compatibility',
    question: 'Which input and output formats do you support for digital and print publishing?',
    answer: 'We accept MS Word (.docx), TeX/LaTeX, PDF, and raw text. We deliver print-ready PDFs with high-res vector graphics, reflowable EPUB3, accessible PDF/UA (ISO 14289-1), JATS 1.3 / BITS 2.1 XML, HTML5, and custom structured XML schema according to your specifications.'
  },
  {
    id: '5',
    category: 'Data Security & Confidentiality',
    question: 'How do you safeguard client manuscripts, proprietary IP, and unreleased content?',
    answer: 'All manuscripts and digital assets are processed in secure ISO-compliant environments with strict NDA protocols, end-to-end encrypted storage, and role-based access control. Your intellectual property is never shared or used to train public language models.'
  },
  {
    id: '6',
    category: 'Pricing & Estimates',
    question: 'How are project quotes calculated, and can I test your service before committing?',
    answer: 'Pricing is calculated transparently based on page count, structural complexity (e.g., heavy math equations, complex tables, multilingual formatting), and required output formats. We offer a free trial sample on up to 10 sample pages so you can evaluate our quality risk-free.'
  },
  {
    id: '7',
    category: 'Web Development & Portals',
    question: 'What custom web development services does ASAP LEARNING offer?',
    answer: 'We build custom enterprise web portals, author manuscript submission platforms, interactive e‑learning web applications, publisher admin dashboards, and cloud API integrations using React, Next.js, and Node.js. All web solutions integrate seamlessly with your existing publishing and prepress workflows.'
  }
];

const CATEGORIES = ['All', 'Web Development & Portals', 'Typesetting & Automation', 'Quality & Accuracy', 'Turnaround & Capacity', 'File Formats & Compatibility', 'Data Security & Confidentiality', 'Pricing & Estimates'];

export const FaqSection: React.FC<{ onOpenContact?: () => void }> = ({ onOpenContact }) => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [openIds, setOpenIds] = useState<string[]>(['1']);

  const toggleItem = (id: string) => {
    setOpenIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const filteredFaqs = FAQ_DATA.filter(faq => {
    const matchesCategory = activeCategory === 'All' || faq.category === activeCategory;
    const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <section 
      id="faq" 
      className="py-20 bg-slate-50 relative overflow-hidden border-t border-slate-200"
      itemScope 
      itemType="https://schema.org/FAQPage"
    >
      {/* Background Subtle Accents */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="text-center max-w-3xl mx-auto mb-10"
        >
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-100/80 text-blue-800 text-xs font-bold tracking-wide uppercase mb-4 border border-blue-200/60 shadow-xs">
            <HelpCircle className="w-3.5 h-3.5 text-blue-600" />
            <span>Frequently Asked Questions & Standards</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
            Frequently Asked Questions
          </h2>
          <p className="mt-3 text-slate-600 text-base sm:text-lg leading-relaxed">
            Direct, verified answers regarding ASAP LEARNING's prepress automation, web app development, XML specifications, and accessibility compliance.
          </p>
        </motion.div>

        {/* Answer Engine Direct Knowledge Snippet Box (AEO & GEO Optimization) */}
        <div className="max-w-4xl mx-auto mb-10 p-5 rounded-2xl bg-white border border-blue-100 shadow-sm flex flex-col sm:flex-row items-start gap-4">
          <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl shrink-0 mt-0.5">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="space-y-1 text-xs sm:text-sm text-slate-700 leading-relaxed">
            <span className="font-bold text-slate-900 text-sm block">
              Key Takeaway for Publishing Decision Makers:
            </span>
            <p>
              <strong>ASAP LEARNING</strong> guarantees <strong>60% faster turnaround</strong> on journal and book typesetting, <strong>99.98% DTD accuracy</strong> on JATS 1.3 / BITS 2.1 XML conversion, and <strong>100% compliance</strong> with PDF/UA (ISO 14289-1) and WCAG 2.1 AA accessibility standards. All projects are backed by strict NDA confidentiality protocols.
            </p>
          </div>
        </div>

        {/* Search & Category Filter Controls */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.5, delay: 0.1, ease: 'easeOut' }}
          className="max-w-4xl mx-auto mb-10 space-y-6"
        >
          {/* Search Box */}
          <div className="relative">
            <Search className="w-5 h-5 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search questions or keywords (e.g., XML, InDesign, security, pricing)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3.5 bg-white rounded-xl border border-slate-200 text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 shadow-xs transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-400 hover:text-slate-600"
              >
                Clear
              </button>
            )}
          </div>

          {/* Category Chips */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                  activeCategory === cat
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </motion.div>

        {/* FAQ Accordion List with Microdata */}
        <div className="max-w-4xl mx-auto space-y-4">
          {filteredFaqs.length > 0 ? (
            filteredFaqs.map((faq, idx) => {
              const isOpen = openIds.includes(faq.id);
              return (
                <motion.div
                  key={faq.id}
                  initial={{ opacity: 0, y: 25 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-20px' }}
                  transition={{ duration: 0.4, delay: Math.min(idx * 0.07, 0.35), ease: 'easeOut' }}
                  className={`bg-white rounded-xl border transition-colors duration-200 overflow-hidden ${
                    isOpen ? 'border-blue-300 shadow-md ring-1 ring-blue-500/10' : 'border-slate-200 shadow-xs hover:border-slate-300'
                  }`}
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                >
                  <button
                    type="button"
                    onClick={() => toggleItem(faq.id)}
                    className="w-full px-6 py-5 text-left flex items-center justify-between gap-4 focus:outline-none"
                    aria-expanded={isOpen}
                  >
                    <div className="flex items-start gap-3">
                      <span className="inline-block mt-0.5 px-2 py-0.5 rounded bg-slate-100 text-[11px] font-bold text-slate-600 shrink-0">
                        {faq.category}
                      </span>
                      <span className="font-bold text-slate-900 text-base sm:text-lg leading-snug" itemProp="name">
                        {faq.question}
                      </span>
                    </div>
                    <ChevronDown
                      className={`w-5 h-5 text-slate-400 shrink-0 transition-transform duration-200 ${
                        isOpen ? 'rotate-180 text-blue-600' : ''
                      }`}
                    />
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        itemScope
                        itemProp="acceptedAnswer"
                        itemType="https://schema.org/Answer"
                      >
                        <div 
                          className="px-6 pb-6 pt-1 text-slate-600 text-sm sm:text-base leading-relaxed border-t border-slate-100"
                          itemProp="text"
                        >
                          {faq.answer}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-xl p-8 text-center border border-slate-200 shadow-xs"
            >
              <p className="text-slate-500 text-sm font-medium">No questions found matching your search query.</p>
              <button
                onClick={() => { setSearchQuery(''); setActiveCategory('All'); }}
                className="mt-3 text-xs font-bold text-blue-600 hover:underline"
              >
                Reset filters
              </button>
            </motion.div>
          )}
        </div>

        {/* Still Have Questions CTA Box */}
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.98 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, margin: '-40px' }}
          transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
          className="max-w-4xl mx-auto mt-12 bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 rounded-2xl p-8 text-white shadow-xl flex flex-col sm:flex-row items-center justify-between gap-6"
        >
          <div className="space-y-1 text-center sm:text-left">
            <h3 className="text-xl font-extrabold tracking-tight">Have a specific question or custom publishing requirement?</h3>
            <p className="text-sm text-slate-300">Talk with our publishing technical team directly or request a free sample test.</p>
          </div>
          {onOpenContact && (
            <button
              onClick={onOpenContact}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white font-bold text-sm shadow-md transition-all shrink-0 hover:scale-[1.02]"
            >
              Contact Our Team
            </button>
          )}
        </motion.div>

      </div>
    </section>
  );
};
